import { defineStore } from 'pinia'
import { asyncRouterMap, constantRouterMap } from '@/router'
import {
  generateRoutesByFrontEnd,
  generateRoutesByServer,
  flatMultiLevelRoutes
} from '@/utils/routerHelper'
import { store } from '../index'
import { cloneDeep } from 'lodash-es'
import { useI18n } from '@/hooks/web/useI18n'
import { Layout } from '@/utils/routerHelper'

export interface PermissionState {
  routers: AppRouteRecordRaw[]
  addRouters: AppRouteRecordRaw[]
  isAddRouters: boolean
  menuTabRouters: AppRouteRecordRaw[]
}

export const usePermissionStore = defineStore('permission', {
  state: (): PermissionState => ({
    routers: [],
    addRouters: [],
    isAddRouters: false,
    menuTabRouters: []
  }),
  getters: {
    getRouters(): AppRouteRecordRaw[] {
      return this.routers
    },
    getAddRouters(): AppRouteRecordRaw[] {
      return flatMultiLevelRoutes(cloneDeep(this.addRouters))
    },
    getIsAddRouters(): boolean {
      return this.isAddRouters
    },
    getMenuTabRouters(): AppRouteRecordRaw[] {
      return this.menuTabRouters
    }
  },
  actions: {
    generateRoutes(
      type: 'server' | 'frontEnd' | 'static',
      routers?: AppCustomRouteRecordRaw[] | string[],
      userStore?: any
    ): Promise<unknown> {
      return new Promise<void>((resolve) => {
        const { t } = useI18n()
        let routerMap: AppRouteRecordRaw[] = []
        if (type === 'server') {
          // 模拟后端过滤菜单
          routerMap = generateRoutesByServer(routers as AppCustomRouteRecordRaw[])
        } else if (type === 'frontEnd') {
          // 模拟前端过滤菜单
          routerMap = generateRoutesByFrontEnd(cloneDeep(asyncRouterMap), routers as string[])
        } else {
          // 直接读取静态路由表
          routerMap = cloneDeep(asyncRouterMap)
          if (userStore) {
            const routerMapFiltered: AppRouteRecordRaw[] = this.checkChildren(routerMap, userStore)
            routerMap = routerMapFiltered
          }
        }
        // 动态路由，404一定要放到最后面
        // keep NoRoutes at the top
        this.addRouters = routerMap.concat([
          {
            path: '/noRoutesParent',
            component: Layout,
            name: 'noRoutesParent',
            meta: {
              hidden: true,
              title: t('login.noRoutes'),
              noTagsView: true
            },
            children: [
              {
                path: '/noRoutes',
                name: 'NoRoutes',
                component: () => import('@/views/Login/NoRoutes.vue'),
                meta: {
                  title: t('login.noRoutes')
                }
              }
            ]
          },
          {
            path: '/noPermissionParent',
            component: Layout,
            name: 'noPermissionParent',
            meta: {
              hidden: true,
              title: t('login.noPermission'),
              noTagsView: true
            },
            children: [
              {
                path: '/noPermission',
                name: 'NoPermission',
                component: () => import('@/views/Login/NoPermission.vue'),
                meta: {
                  title: t('login.noPermission')
                }
              }
            ]
          },
          {
            path: '/:path(.*)*',
            redirect: '/404',
            name: '404Page',
            meta: {
              hidden: true,
              breadcrumb: false
            }
          }
        ])
        // 渲染菜单的所有路由
        this.setRouters(cloneDeep(constantRouterMap).concat(routerMap))
        resolve()
      })
    },
    setRouters(routers: AppRouteRecordRaw[]) {
      this.routers = routers
    },
    checkChildren(children: AppRouteRecordRaw[], userStore: any) {
      const retChildren: AppRouteRecordRaw[] = []
      children.forEach((route) => {
        const permission = route.meta?.owPermission
        // only accessible rout can be added.
        if (!permission || (permission && userStore?.hasPermission(permission))) {
          if (route.children) {
            const ret = this.checkChildren(route.children, userStore)
            if (ret.length != 0) {
              route.children = ret
              retChildren.push(route)
            }
          } else {
            retChildren.push(route)
          }
        }
      })
      return retChildren
    },
    setIsAddRouters(state: boolean): void {
      this.isAddRouters = state
    },
    setMenuTabRouters(routers: AppRouteRecordRaw[]): void {
      this.menuTabRouters = routers
    }
  },
  persist: {
    paths: ['routers', 'addRouters', 'menuTabRouters']
  }
})

export const usePermissionStoreWithOut = () => {
  return usePermissionStore(store)
}
